/*
  NewDecompte.jsx
  ----------------
  Interface de création d'un décompte.
*/
import { useState, useEffect } from "react";
import {
  Card,
  TextInput,
  Button,
  Stack,
  Group,
  Text,
  Combobox,
  InputBase,
  Pill,
  PillGroup,
  Center,
  Select,
  Title,
  Box,
  Accordion,
} from "@mantine/core";
import { Dropzone } from "@mantine/dropzone";
import { IconCheck, IconX } from "@tabler/icons-react";
import { get, post, uploadFile } from "../tools/Request";
import { useUser } from "../tools/Context";
import { showNotification } from "@mantine/notifications";
import { useNavigate, useLocation } from "react-router-dom";

export default function NewDecompte() {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const marketIdFromUrl = queryParams.get("marketId");

  const [name, setName] = useState("");
  const [filesToUpload, setFilesToUpload] = useState([]);
  const [existingDocs, setExistingDocs] = useState([]);
  const [marketId, setMarketId] = useState(marketIdFromUrl || null);
  const [markets, setMarkets] = useState([]);

  const [signerOptions, setSignerOptions] = useState([]);
  const [titulaireOptions, setTitulaireOptions] = useState([]);

  const [selectedWorkflow, setSelectedWorkflow] = useState([]);
  const [selectedTitulaire, setSelectedTitulaire] = useState(null);

  const [comboboxOpened, setComboboxOpened] = useState(false);
  const [linkedDecompteId, setLinkedDecompteId] = useState(null);
  const [myDecomptes, setMyDecomptes] = useState([]);
  const [prefillDecompteId, setPrefillDecompteId] = useState(null);

  const user = useUser();
  const navigate = useNavigate();

  const getBusinessRoleById = (id) => {
    const fromSigner = signerOptions.find((o) => o.id === id);
    if (fromSigner) return fromSigner.businessRole;
    const fromTitulaire = titulaireOptions.find((o) => o.id === id);
    if (fromTitulaire) return fromTitulaire.businessRole;
    return undefined;
  };

  useEffect(() => {
    const fetchMyDecomptes = async () => {
      try {
        const result = await get("decompte/my-decomptes", null, user.token);
        setMyDecomptes(result);
      } catch (err) {
        console.error("Error fetching my decomptes:", err);
      }
    };
    fetchMyDecomptes();
  }, [user.token]);

  useEffect(() => {
    const fetchMarkets = async () => {
      try {
        const response = await get("market", null, user.token);
        setMarkets(response);
      } catch (error) {
        console.error("Error fetching markets:", error);
      }
    };
    fetchMarkets();
  }, [user.token]);

  useEffect(() => {
    const fetchAuthorizedUsers = async () => {
      if (!marketId) {
        setSignerOptions([]);
        setTitulaireOptions([]);
        return;
      }
      try {
        const selectedMarket = markets.find((m) => m.id === marketId);
        if (!selectedMarket) return;

        const response = await get("users/getAllUser", null, user.token);

        const filtered = response
          .filter((u) => u.authorized_markets.includes(selectedMarket.market_number))
          .filter((u) => u.email !== user.email);

        const titulaires = filtered.filter((u) => u.role === "titulaire de marché");
        const signers = filtered.filter((u) => u.role !== "titulaire de marché");

        const mappedTitulaires = titulaires.map((u) => ({
          id: u.id,
          email: u.email,
          name: `${u.firstname} ${u.lastname}`,
          businessRole: u.role,
        }));
        setTitulaireOptions(mappedTitulaires);

        if (mappedTitulaires.length === 1) {
          setSelectedTitulaire(mappedTitulaires[0].id);
        } else {
          setSelectedTitulaire(null);
        }

        setSignerOptions(
          signers.map((u) => ({
            id: u.id,
            email: u.email,
            name: `${u.firstname} ${u.lastname}`,
            businessRole: u.role,
          }))
        );
      } catch (error) {
        console.error("Error fetching users:", error);
      }
    };

    fetchAuthorizedUsers();
  }, [marketId, markets, user.token, user.email]);

  useEffect(() => {
    setSelectedWorkflow((prev) =>
      prev.map((s) => ({
        ...s,
        userRole: s.userRole || getBusinessRoleById(s.id) || s.userRole,
      }))
    );
  }, [signerOptions, titulaireOptions]);

  const handleAddSigner = (signer) => {
    if (!selectedWorkflow.find((s) => s.id === signer.id)) {
      setSelectedWorkflow([
        ...selectedWorkflow,
        { id: signer.id, email: signer.email, role: "Signataire", userRole: signer.businessRole },
      ]);
    }
    setComboboxOpened(false);
  };

  const handleRemoveSigner = (id) => {
    setSelectedWorkflow((prev) => prev.filter((s) => s.id !== id));
  };

  const handleDownloadExisting = (docId) => {
    const link = document.createElement("a");
    link.href = `/api/file/download/${docId}`;
    link.target = "_blank";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleSend = async () => {
    if (
      !name ||
      !marketId ||
      selectedWorkflow.length === 0 ||
      (filesToUpload.length === 0 && existingDocs.length === 0)
    ) {
      showNotification({
        title: "Champs requis",
        message: "Veuillez remplir tous les champs obligatoires.",
        color: "orange",
        icon: <IconX size={18} />,
      });
      return;
    }

    try {
      const formData = new FormData();
      formData.append("name", name);
      formData.append("linked_decompte", linkedDecompteId || "");
      formData.append("market_id", marketId);

      if (prefillDecompteId) {
        formData.append("clone_from", prefillDecompteId);
      }

      if (existingDocs.length > 0) {
        const docIdsToClone = existingDocs.filter((d) => d.id !== undefined && d.id !== null).map((d) => d.id);
        if (docIdsToClone.length > 0) {
          formData.append("clone_document_ids", JSON.stringify(docIdsToClone));
        }
      }

      filesToUpload.forEach((f) => formData.append("files", f));

      const decompte = await uploadFile("decompte/create", formData, user.token);

      const signerPayload = {
        signers: [
          {
            signer_id: user.id,
            order_index: 1,
            role: "Signataire",
          },
          ...selectedWorkflow.map((s, i) => ({
            signer_id: s.id,
            order_index: i + 2,
            role: s.role === "Lecteur" ? "Lecteur" : "Signataire",
          })),
          ...(selectedTitulaire
            ? [
                {
                  signer_id: selectedTitulaire,
                  order_index: selectedWorkflow.length + 2,
                  role: "Signataire",
                },
              ]
            : []),
        ],
      };

      await post(`decompte/add-signers/${decompte.id}`, signerPayload, user.token);

      showNotification({
        title: "Succès",
        message: "Décompte et signataires enregistrés !",
        color: "green",
        icon: <IconCheck size={18} />,
      });

      navigate(`/myDecompte?search=${encodeURIComponent(name)}`);
    } catch (error) {
      console.error("Erreur lors de l'envoi:", error);
      showNotification({
        title: "Erreur",
        message: "Une erreur est survenue lors de l'envoi.",
        color: "red",
        icon: <IconX size={18} />,
      });
    }
  };

  const workflowDisabled = !marketId || signerOptions.length === 0;
  const workflowError =
    marketId && signerOptions.length === 0 ? "Aucun utilisateur disponible pour ce marché" : undefined;

  const selectedTitulaireEmail = selectedTitulaire && titulaireOptions.find((t) => t.id === selectedTitulaire)?.email;
  const selectedTitulaireBusinessRole =
    selectedTitulaire && titulaireOptions.find((t) => t.id === selectedTitulaire)?.businessRole;

  return (
    <>
      <Center>
        <Title>Créer un décompte</Title>
      </Center>
      <Center>
        <Card shadow="sm" padding="lg" radius="md" withBorder w="80%" mt={40}>
          <Stack gap="md">
            <TextInput
              label="Nom du décompte"
              placeholder="Entrer le nom du décompte"
              value={name}
              onChange={(event) => setName(event.currentTarget.value)}
            />

            <Select
              label="Marché"
              placeholder="Sélectionner un marché"
              data={markets.map((m) => ({ value: m.id, label: m.market_number }))}
              value={marketId}
              onChange={setMarketId}
              searchable
              clearable
            />

            <Select
              label="Remplir depuis un décompte"
              placeholder="Choisir un décompte existant"
              data={myDecomptes.filter((d) => d.id !== linkedDecompteId).map((d) => ({ value: d.id, label: d.name }))}
              value={prefillDecompteId}
              onChange={async (selectedId) => {
                setPrefillDecompteId(selectedId);
                if (!selectedId) return;
                try {
                  const result = await get(`decompte/${selectedId}`, null, user.token);
                  const { decompte, signers } = result;

                  setMarketId(decompte.market_id);
                  setLinkedDecompteId(decompte.linked_decompte || null);

                  const newWorkflow = signers
                    .filter((s) => s.signer_id !== user.id)
                    .slice(0, -1)
                    .map((s) => {
                      const businessRole = getBusinessRoleById(s.signer_id) || s.user_role || undefined;
                      return {
                        id: s.signer_id,
                        email: s.email ?? `${s.firstname}.${s.lastname}@example.com`,
                        role: s.role === "viewer" ? "Lecteur" : "Signataire",
                        userRole: businessRole,
                      };
                    });

                  setSelectedWorkflow(newWorkflow);

                  const sortedSigners = [...signers].sort((a, b) => b.order_index - a.order_index);
                  const lastSigner = sortedSigners.find((s) => (s.role || "").toLowerCase() === "signer");
                  if (lastSigner) {
                    setSelectedTitulaire(lastSigner.signer_id);
                  }

                  const docs = await get(`decompte/file/decompte-documents/${selectedId}`, null, user.token);
                  setExistingDocs(docs);

                  showNotification({
                    title: "Champs remplis",
                    message: "Formulaire rempli à partir du décompte sélectionné.",
                    color: "green",
                  });
                } catch (err) {
                  console.error("Erreur de préremplissage:", err);
                  showNotification({
                    title: "Erreur",
                    message: "Impossible de remplir depuis ce décompte.",
                    color: "red",
                  });
                }
              }}
              searchable
              clearable
              nothingFound="Aucun décompte disponible"
            />

            <Box p="md" mt="md" style={{ border: "1.5px solid #eaeaea", borderRadius: "8px" }}>
              <Text fw={500} mb="sm">
                F – Pièces jointes
              </Text>

              {existingDocs.length > 0 && (
                <Accordion variant="contained" mt="md">
                  {existingDocs.map((doc, index) => (
                    <Accordion.Item value={`existing-${index}`} key={`existing-${doc.id || index}`}>
                      <Accordion.Control>
                        <Text fw={500}>{doc.name}</Text>
                      </Accordion.Control>
                      <Accordion.Panel>
                        <Box mt="md" style={{ display: "flex", justifyContent: "flex-end", gap: 12 }}>
                          <Button variant="light" color="blue" onClick={() => handleDownloadExisting(doc.id)}>
                            Télécharger le fichier
                          </Button>
                          <Button
                            variant="light"
                            color="#e2231a"
                            onClick={() => setExistingDocs((prev) => prev.filter((_, i) => i !== index))}
                          >
                            Retirer de cette copie
                          </Button>
                        </Box>
                      </Accordion.Panel>
                    </Accordion.Item>
                  ))}
                </Accordion>
              )}

              {filesToUpload.length > 0 && (
                <Accordion variant="contained" mt="md">
                  {filesToUpload.map((file, index) => (
                    <Accordion.Item value={`new-${index}`} key={`new-${index}-${file.name}`}>
                      <Accordion.Control>
                        <Text fw={500}>{file.name}</Text>
                      </Accordion.Control>
                      <Accordion.Panel>
                        <Box mt="md" style={{ display: "flex", justifyContent: "flex-end", gap: 12 }}>
                          <Button
                            variant="light"
                            color="blue"
                            onClick={() => {
                              const url = URL.createObjectURL(file);
                              const a = document.createElement("a");
                              a.href = url;
                              a.download = file.name;
                              document.body.appendChild(a);
                              a.click();
                              a.remove();
                              URL.revokeObjectURL(url);
                            }}
                          >
                            Télécharger le fichier
                          </Button>
                          <Button
                            variant="light"
                            color="#e2231a"
                            onClick={() => setFilesToUpload((prev) => prev.filter((_, i) => i !== index))}
                          >
                            Supprimer le fichier
                          </Button>
                        </Box>
                      </Accordion.Panel>
                    </Accordion.Item>
                  ))}
                </Accordion>
              )}

              <Dropzone
                mt={20}
                onDrop={(dropped) => setFilesToUpload((prev) => [...prev, ...dropped])}
                onReject={(rejected) => console.log("Rejected files:", rejected)}
                maxSize={5 * 1024 ** 2}
                multiple
                disabled={!marketId}
              >
                <Center p="md">
                  <Text size="md" c="dimmed">
                    Ajoutez vos pièces jointes ici
                  </Text>
                </Center>
              </Dropzone>
            </Box>

            <div>
              <Text fw={500} size="sm" mb={4}>
                Workflow
              </Text>
              <Combobox
                opened={comboboxOpened}
                onDropdownClose={() => setComboboxOpened(false)}
                onDropdownOpen={() => setComboboxOpened(true)}
                disabled={workflowDisabled}
              >
                <Combobox.Target>
                  <InputBase
                    component="button"
                    type="button"
                    pointer
                    onClick={() => {
                      if (!workflowDisabled) setComboboxOpened((o) => !o);
                    }}
                    error={workflowError}
                    disabled={workflowDisabled}
                  >
                    Ajouter une personne au workflow
                  </InputBase>
                </Combobox.Target>
                <Combobox.Dropdown>
                  <Combobox.Options>
                    {signerOptions.length === 0 ? (
                      <Combobox.Empty>Aucun utilisateur disponible</Combobox.Empty>
                    ) : (
                      signerOptions.map((signer) => (
                        <Combobox.Option
                          key={signer.id}
                          value={String(signer.id)}
                          onClick={() => handleAddSigner(signer)}
                        >
                          {signer.email} ({signer.businessRole})
                        </Combobox.Option>
                      ))
                    )}
                  </Combobox.Options>
                </Combobox.Dropdown>
              </Combobox>

              {selectedWorkflow.length > 0 && (
                <PillGroup mt="sm">
                  {selectedWorkflow.map((signer, index) => (
                    <div key={signer.id}>
                      <Pill withRemoveButton onRemove={() => handleRemoveSigner(signer.id)}>
                        {index + 2}. {signer.email} ({signer.userRole || "—"} • {signer.role})
                      </Pill>
                      <Select
                        data={[
                          { value: "Signataire", label: "Signataire" },
                          { value: "Lecteur", label: "Lecteur" },
                        ]}
                        value={signer.role}
                        onChange={(role) =>
                          setSelectedWorkflow((prev) => prev.map((s) => (s.id === signer.id ? { ...s, role } : s)))
                        }
                        size="xs"
                        mt={4}
                      />
                    </div>
                  ))}
                </PillGroup>
              )}
            </div>

            <Select
              label="Titulaire de marché"
              placeholder="Sélectionner un titulaire"
              data={titulaireOptions.map((t) => ({
                value: t.id,
                label: `${t.email} (${t.businessRole})`,
              }))}
              value={selectedTitulaire}
              onChange={setSelectedTitulaire}
              searchable
              clearable
              nothingFound="Aucun titulaire trouvé"
              disabled={!marketId}
            />
            {selectedTitulaireEmail && (
              <Text size="sm" c="dimmed">
                Titulaire sélectionné : {selectedTitulaireEmail} ({selectedTitulaireBusinessRole || "titulaire"})
              </Text>
            )}

            <Select
              label="Lier à un décompte"
              placeholder="Sélectionner un décompte existant"
              data={myDecomptes.map((d) => ({ value: d.id, label: d.name }))}
              value={linkedDecompteId}
              onChange={setLinkedDecompteId}
              searchable
              clearable
            />

            <Group justify="space-between" mt="md">
              <Button
                onClick={handleSend}
                disabled={
                  !name ||
                  !marketId ||
                  selectedWorkflow.length === 0 ||
                  (filesToUpload.length === 0 && existingDocs.length === 0)
                }
              >
                Envoyer
              </Button>
            </Group>
          </Stack>
        </Card>
      </Center>
    </>
  );
}
